<template>
  <div class="flex flex-wrap justify-end">
    <o-tooltip v-if="!customer.isArchived" :text="$t('components.customer.profile.actions.editRecord')" class="mr-1 mb-1">
      <o-panel-link tag="o-button" :path="`/customer/edit?id=${customer.idCustomer}`" icon="edit">
        {{ $t('general.edit') }}
      </o-panel-link>
    </o-tooltip>

    <o-tooltip v-if="customer.isArchived" :text="$t('components.customer.profile.actions.restoreCustomerAccount')" class="mr-1 mb-1">
      <o-button icon="archive" variant="warning" @click="handleRestoreCustomer">
        {{ $t('general.restore') }}
      </o-button>
    </o-tooltip>

    <o-tooltip v-if="!customer.isArchived" :text="$t('auth.password.resetPassword.title')" class="mr-1 mb-1">
      <o-button
        :flat="!!panel"
        size="slim"
        icon="customerResetPass"
        @click="handleResetPassword"
      />
    </o-tooltip>

    <o-tooltip v-if="$gates.all(['messaging'])" :text="$t('general.messages')" class="mr-1 mb-1">
      <o-panel-link
        :path="`/message/chat/${customer.idCustomer}`"
        tag="o-button"
        size="slim"
        icon="menuMessage"
      />
    </o-tooltip>

    <o-dropdown
      placement="bottom-end"
      class="mr-1 mb-1"
    >
      <o-button
        :flat="!!panel"
        size="slim"
        icon="ellipses"
      />

      <template #content>
        <o-history-link :item="customer" model="Customer" component="ODropdownItem" :description="$t('components.customer.profile.actions.seeAllChanges')" icon="history" />

        <o-dropdown-divider />

        <o-dropdown-item v-if="!customer.isArchived" icon="email" :description="$t('components.customer.profile.actions.resetsOnlineBookingPassword')" @click="handleSendWelcomeEmail">
          {{ $t('components.customer.profile.actions.resendWelcomeEmail') }}
        </o-dropdown-item>

        <o-panel-link
          v-if="!customer.isArchived && $grants.all({ export: ['customerSingle'] })"
          path="/export"
          tag="o-dropdown-item"
          icon="export"
          :description="$t('components.customer.profile.actions.exportInfo')"
          :props="{
            presets: {
              type: 'customerSingle',
              params: {
                idCustomer: customer
              }
            }
          }"
        >
          {{ $t('components.customer.profile.actions.export') }}
        </o-panel-link>

        <o-dropdown-divider v-if="!customer.isArchived" />

        <o-dropdown-item v-if="!customer.isArchived" icon="archive" :description="$t('components.customer.profile.actions.archiveInfo')" @click="handleArchiveCustomer">
          {{ $t('components.customer.profile.actions.archive') }}
        </o-dropdown-item>
        <o-dropdown-item v-if="customer.isArchived" icon="archive" :description="$t('components.customer.profile.actions.restoreInfo')" @click="handleRestoreCustomer">
          {{ $t('components.customer.profile.actions.restore') }}
        </o-dropdown-item>
        <o-dropdown-item icon="delete" :description="$t('components.customer.profile.actions.deleteInfo')" @click="handleDeleteCustomer">
          {{ $t('components.customer.profile.actions.delete') }}
        </o-dropdown-item>
      </template>
    </o-dropdown>

    <SendWelcomeEmailDialog
      ref="sendWelcomeEmailDialog"
      :customer="customer"
      @update:customer="$emit('update:customer', $event)"
      @update:loading="$emit('update:loading', $event)"
    />

    <ResetPasswordDialog
      ref="resetPasswordDialog"
      :customer="customer"
      @update:customer="$emit('update:customer', $event)"
      @update:loading="$emit('update:loading', $event)"
    />

    <ArchiveCustomerDialog
      ref="archiveCustomerDialog"
      :customer="customer"
      @update:customer="$emit('update:customer', $event)"
      @update:loading="$emit('update:loading', $event)"
    />

    <RestoreCustomerDialog
      ref="restoreCustomerDialog"
      :customer="customer"
      @update:customer="$emit('update:customer', $event)"
      @update:loading="$emit('update:loading', $event)"
    />

    <DeleteCustomerDialog
      ref="deleteCustomerDialog"
      :customer="customer"
      @update:customer="$emit('update:customer', $event)"
      @update:loading="$emit('update:loading', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import SendWelcomeEmailDialog from '@/components/customer/actions/SendWelcomeEmail.vue'
import ResetPasswordDialog from '@/components/customer/actions/ResetPassword.vue'
import ArchiveCustomerDialog from '@/components/customer/actions/ArchiveCustomer.vue'
import RestoreCustomerDialog from '@/components/customer/actions/RestoreCustomer.vue'
import DeleteCustomerDialog from '@/components/customer/actions/DeleteCustomer.vue'

import {
  ODropdown,
  ODropdownItem,
  ODropdownDivider,
} from '@/components/elements'

interface Props {
  customer: Record<string, any>
}

defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:customer', value: any): void
  (e: 'update:loading', value: any): void
}>()

const panel = inject('panel', null)

const sendWelcomeEmailDialog = ref<any>()
const resetPasswordDialog = ref<any>()
const archiveCustomerDialog = ref<any>()
const restoreCustomerDialog = ref<any>()
const deleteCustomerDialog = ref<any>()

const handleArchiveCustomer = () => {
  archiveCustomerDialog.value.open()
}

const handleRestoreCustomer = () => {
  restoreCustomerDialog.value.open()
}

const handleDeleteCustomer = () => {
  deleteCustomerDialog.value.open()
}

const handleResetPassword = () => {
  resetPasswordDialog.value.open()
}

const handleSendWelcomeEmail = () => {
  sendWelcomeEmailDialog.value.open()
}
</script>
